import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getShop, buyItem } from "@/lib/game/shop.functions";
import { Button } from "@/components/ui/button";
import { Coins, ShoppingCart, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { RouteError, RouteNotFound } from "@/components/game/RouteError";
import { InfoTip } from "@/components/game/InfoTip";
import { Panel } from "@/components/game/Panel";
import { RarityTag } from "@/components/game/Badges";
import {
  GoldAmount,
  ItemIcon,
  MicroLabel,
  PageHeader,
  ScreenContainer,
} from "@/components/game/ScreenKit";
import { SHOP_TIPS, RARITY_TIPS } from "@/lib/game/tooltips";
import { currentLang } from "@/lib/i18n/lang";
import { useTutorial } from "@/components/game/TutorialProvider";
import { tServerError } from "@/lib/i18n/server-error";

export const Route = createFileRoute("/_app/game/shop")({
  component: Shop,
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
});

/**
 * Die Rarität kommt als JSON-Wert vom Server zurück. `RarityTag` und `ItemIcon`
 * erwarten dagegen einen der vier bekannten Werte — diese Tabelle bildet ihn
 * ohne `as`-Cast ab und fällt auf `common` zurück, falls je ein unbekannter
 * Wert ankommt (dann sieht das Item gewöhnlich aus, statt dass die Kachel
 * farblos bricht).
 */
const RARITY: Record<string, "common" | "rare" | "epic" | "legendary"> = {
  common: "common",
  rare: "rare",
  epic: "epic",
  legendary: "legendary",
};

/**
 * ⚠️ `RarityTag` OHNE `label` fällt auf seine fest deutschen Labels zurück
 * (`RARITY_LABELS` in `components/game/Badges.tsx`, nicht exportiert und
 * nirgends übersetzt) — in der englischen Fassung stand deshalb „GEWÖHNLICH"
 * neben englischem Text, während dieselbe Rarität in der Brutkammer korrekt
 * „Common" heißt.
 *
 * 📌 Zwischenlösung mit demselben Muster wie `_app.game.breeding.tsx`: Der
 * Aufrufer bringt die Übersetzung mit. Der saubere Weg wäre, die vier Namen
 * einmal als i18n-Schlüssel anzulegen und direkt in `Badges.tsx` zu lesen —
 * das ist eine Änderung an gemeinsamen Dateien und steht als offener Punkt im
 * Ergebnis.
 */
const RARITY_LABELS: Record<string, { de: string; en: string }> = {
  common: { de: "Gewöhnlich", en: "Common" },
  rare: { de: "Selten", en: "Rare" },
  epic: { de: "Episch", en: "Epic" },
  legendary: { de: "Legendär", en: "Legendary" },
};

function rarityLabel(rarity: string): string {
  const b = RARITY_LABELS[rarity];
  if (!b) return rarity;
  return currentLang() === "en" ? b.en : b.de;
}

function Shop() {
  useTutorial("shop_intro");
  const { t } = useTranslation("game");
  const qc = useQueryClient();

  const fetchShop = useServerFn(getShop);
  const buyFn = useServerFn(buyItem);

  const shopQ = useQuery({ queryKey: ["shop"], queryFn: () => fetchShop() });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["shop"] });
    qc.invalidateQueries({ queryKey: ["items"] });
    qc.invalidateQueries({ queryKey: ["combat-state"] });
    qc.invalidateQueries({ queryKey: ["my-monster"] });
  };

  const buyMut = useMutation({
    mutationFn: (slot: number) => buyFn({ data: { slot } }),
    onSuccess: () => {
      toast.success(t("shop.toast.bought"));
      invalidate();
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  return (
    <ScreenContainer>
      <PageHeader
        title={t("nav.shop")}
        icon={<ShoppingCart className="h-6 w-6 shrink-0" strokeWidth={2.5} />}
        sub={
          <>
            <InfoTip text={SHOP_TIPS.daily}>
              <span className="underline decoration-dotted underline-offset-2">
                {t("shop.hourlyOffer")}
              </span>
            </InfoTip>{" "}
            {t("shop.subtitle")} {t("shop.sellHintPre")}{" "}
            <Link
              to="/game/inventory"
              className="font-extrabold text-primary underline underline-offset-2"
            >
              {t("nav.inventory")}
            </Link>
            .
          </>
        }
        actions={
          /* Der eigene Goldstand ist hier keine Deko: An ihm hängt, welcher
             Kauf-Knopf „Zu teuer" zeigt. Erst rendern, wenn der Wert wirklich
             da ist — eine 0 während des Ladens wäre eine falsche Aussage. */
          shopQ.data ? (
            <span className="inline-flex min-h-9 items-center rounded-full border-2 border-ink bg-ink px-3">
              <GoldAmount value={shopQ.data.gold} size="md" />
            </span>
          ) : null
        }
      />

      {/* Verweis auf den Gold-Shop: eigener Ton (boss), damit er sich als
          Echtgeld-Bereich vom Item-Angebot abhebt, und die einzige
          Gold-Variante des Buttons auf dem Screen. */}
      <Panel tone="boss" className="mb-4" contentClassName="p-3.5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-md border-2 border-ink bg-gold text-ink">
            <Coins className="h-5 w-5" strokeWidth={2.5} />
          </div>
          <div className="min-w-[180px] flex-1">
            <h3 className="text-[17px] leading-tight">{t("shop.goldRecharge")}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground [overflow-wrap:anywhere]">
              {t("shop.goldRechargeSubtitle")}
            </p>
          </div>
          <Button asChild variant="gold" size="sm" className="min-h-11 sm:min-h-9">
            <Link to="/game/gold-shop">{t("nav.goldShop")}</Link>
          </Button>
        </div>
      </Panel>

      <div className="mb-2.5 flex items-center gap-2">
        <Sparkles className="h-4 w-4 shrink-0 text-primary" strokeWidth={2.5} />
        <h3 className="text-[17px] leading-tight">{t("shop.offerLabel")}</h3>
      </div>

      <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3.5">
        {shopQ.isLoading && (
          <p className="col-span-full text-sm text-muted-foreground">{t("shop.loading")}</p>
        )}
        {shopQ.data?.shop.map((s) => {
          const isConsumable = s.kind === "consumable";
          // Nur Ausrüstung ist pro Stunde einmal kaufbar; Verbrauchsitems
          // stapeln sich und bleiben deshalb immer kaufbar.
          const bought = !isConsumable && shopQ.data.purchases.includes(s.slot);
          const canAfford = (shopQ.data?.gold ?? 0) >= s.price;
          const blocked = bought || !canAfford;
          // Ohne spd: spd wird im Zonenkampf in keiner Formel gelesen (Stefan, 09.09.2026). Filtern statt nur das Label zu loeschen —
          // sonst faellt die Uebersetzung auf den Rohschluessel zurueck und zeigt "spd".
          const stats = Object.entries(s.stats ?? {}).filter(([k]) => k !== "spd");
          const rarity = RARITY[s.rarity] ?? "common";
          return (
            <motion.div
              key={s.slot}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: s.slot * 0.05 }}
              className="min-w-0"
            >
              <Panel
                tone={rarity === "legendary" ? "boss" : "default"}
                holo={rarity === "legendary"}
                contentClassName="p-0"
                className={bought ? "opacity-55" : undefined}
              >
                {/* Art-Fläche: Ink-Grund, damit die Raritätsfarbe der Kachel
                    trägt. Das Emoji ist der bestehende Platzhalter aus den
                    Shop-Daten — echte Item-Grafiken gibt es nicht. */}
                <div className="grid place-items-center bg-ink p-4">
                  <ItemIcon
                    icon={<span className="text-3xl leading-none">{s.icon}</span>}
                    rarity={rarity}
                    size={64}
                  />
                </div>

                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className="line-clamp-2 min-w-0 text-sm font-extrabold leading-snug [overflow-wrap:anywhere]">
                      {s.name}
                    </span>
                    <InfoTip text={RARITY_TIPS[rarity] ?? ""}>
                      <RarityTag rarity={rarity} label={rarityLabel(rarity)} className="shrink-0" />
                    </InfoTip>
                  </div>

                  <MicroLabel className="mt-1.5">
                    <InfoTip text={t(`inventory.kindTips.${s.kind}`, "")}>
                      <span className="underline decoration-dotted underline-offset-2">
                        {t(`inventory.kinds.${s.kind}`, s.kind)}
                      </span>
                    </InfoTip>
                  </MicroLabel>

                  {/* Ausrüstung: dieselben Werte wie im Inventar, mit den dort
                      schon vorhandenen Stat-Labels statt der rohen Server-
                      Beschreibung („+6 hp_max"). Verbrauchsitems tragen ihren
                      Effekt als Text („Voll-Heilung"), der sich nicht aus den
                      Stats ableiten lässt. */}
                  {!isConsumable && stats.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {stats.map(([k, v]) => (
                        <span
                          key={k}
                          className="rounded-full border-2 border-ink bg-secondary px-2 py-px text-[11px] font-extrabold tabular-nums"
                        >
                          +{v} {t(`inventory.stats.${k}`, k)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-2 text-[11px] text-muted-foreground [overflow-wrap:anywhere]">
                      {s.description}
                    </p>
                  )}

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <GoldAmount value={s.price} size="md" />
                    {/* Bewusst `secondary` statt `default`: Das Angebot besteht aus
                        15 gleichrangigen Karten (9 Verbrauchsitems + 6 Ausrüstung),
                        `default` wäre bg-primary — bei genug Gold stünden hier bis zu
                        15 orange Knöpfe gleichzeitig. Der Kanon lässt höchstens EINEN
                        orangenen Button pro Ansicht zu (design-system/readme.md,
                        handoff/HANDOFF.md „Akzeptanzkriterien"). Die Vorlage
                        (ui_kits/xenomons/EconomyScreens.jsx) setzt hier `default` und
                        widerspricht damit ihrem eigenen Kanon — es gilt der Kanon.
                        Kaufbar vs. gesperrt trennt weiterhin der Disabled-Zustand
                        (opacity .45) plus die Beschriftung; den Preis trägt ohnehin
                        die Gold-Farbe daneben. */}
                    <Button
                      size="sm"
                      variant="secondary"
                      className="min-h-11 sm:min-h-9"
                      disabled={blocked || buyMut.isPending}
                      onClick={() => buyMut.mutate(s.slot)}
                    >
                      {bought
                        ? t("shop.btnBought")
                        : !canAfford
                          ? t("shop.btnTooExpensive")
                          : t("shop.btnBuy")}
                    </Button>
                  </div>
                </div>
              </Panel>
            </motion.div>
          );
        })}
      </div>
    </ScreenContainer>
  );
}
